import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import receiversPollersService from '../services/receiversPollersService';

// Receivers / Pollers — ESB infrastructure that ingests data. Admin/Infra list of every receiver &
// poller. Add opens a full page (per STYLE.md — no modals).
const scopeBadge = (row) => {
    if (row.hotel_id) return { label: 'Hotel', name: row.hotel_name, color: '#0e7490', bg: '#ecfeff' };
    if (row.account_id) return { label: 'Chain', name: row.account_name, color: '#6d28d9', bg: '#f5f3ff' };
    if (row.partner_id) return { label: 'Partner', name: row.partner_name, color: '#b45309', bg: '#fffbeb' };
    return { label: 'Global', name: null, color: '#374151', bg: '#f3f4f6' };
};

const ReceiversPollersPage = () => {
    const navigate = useNavigate();
    const [items, setItems] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await receiversPollersService.list();
            setItems(res.receiversPollers || []);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to load receivers/pollers');
            setItems(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <div style={{ padding: '2rem', maxWidth: 1100 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                    <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1e293b', margin: '0 0 0.5rem' }}>Receivers / Pollers</h1>
                    <p style={{ color: '#6b7280', margin: 0 }}>
                        Receivers (push endpoints) and pollers (scheduled fetchers) that ingest data into the platform.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => navigate('/admin/receivers-pollers/new')}
                    style={{
                        flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '0.45rem 0.9rem', fontSize: '0.875rem', fontWeight: 600,
                        color: '#fff', background: '#4a6cf7', border: 'none', borderRadius: 7, cursor: 'pointer',
                    }}
                >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                    Add Receiver/Poller
                </button>
            </div>

            {error ? (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.9rem' }}>{error}</div>
            ) : items === null || loading ? (
                <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Loading…</p>
            ) : items.length === 0 ? (
                <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1.5rem', color: '#6b7280', fontSize: '0.925rem' }}>
                    No receivers or pollers yet.
                </div>
            ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                    <thead>
                        <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
                            <th style={{ padding: '0.6rem 0.75rem', color: '#374151', fontWeight: 600 }}>Name</th>
                            <th style={{ padding: '0.6rem 0.75rem', color: '#374151', fontWeight: 600 }}>Kind</th>
                            <th style={{ padding: '0.6rem 0.75rem', color: '#374151', fontWeight: 600 }}>Scope</th>
                            <th style={{ padding: '0.6rem 0.75rem', color: '#374151', fontWeight: 600 }}>Status</th>
                            <th style={{ padding: '0.6rem 0.75rem' }} />
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((row, i) => {
                            const scope = scopeBadge(row);
                            return (
                                <tr key={row.id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                                    <td style={{ padding: '0.6rem 0.75rem', color: '#111827', fontWeight: 600 }}>{row.name}</td>
                                    <td style={{ padding: '0.6rem 0.75rem', color: '#374151', textTransform: 'capitalize' }}>{row.kind}</td>
                                    <td style={{ padding: '0.6rem 0.75rem' }}>
                                        <span style={{ display: 'inline-block', padding: '0.15rem 0.55rem', borderRadius: 999, fontSize: '0.78rem', fontWeight: 600, color: scope.color, background: scope.bg }}>
                                            {scope.label}{scope.name ? ` · ${scope.name}` : ''}
                                        </span>
                                    </td>
                                    <td style={{ padding: '0.6rem 0.75rem' }}>
                                        <span style={{ display: 'inline-block', padding: '0.15rem 0.55rem', borderRadius: 999, fontSize: '0.78rem', fontWeight: 600, color: row.status === 'active' ? '#166534' : '#6b7280', background: row.status === 'active' ? '#dcfce7' : '#f3f4f6' }}>
                                            {row.status}
                                        </span>
                                    </td>
                                    <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        <button
                                            type="button"
                                            onClick={() => navigate(`/admin/receivers-pollers/${row.id}/edit`)}
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '0.3rem 0.7rem', fontSize: '0.8rem', fontWeight: 600, color: '#4a6cf7', background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' }}
                                        >
                                            <svg width="13" height="13" viewBox="0 0 20 20" fill="none"><path d="M14 3l3 3-9 9H5v-3l9-9z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                            Edit
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default ReceiversPollersPage;
